import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { Button, Grid, Stack } from '@mui/material';

import { CfsApi, IncomingCallsApi } from '@/api';
import { FormCard, FormRoot } from '@/components';
import withUnsavedChangesWarning, {
  WithUnsavedChangesWarningProps,
} from '@/hocs/withUnsavedChangesWarning';
import {
  IAddress,
  IMasterNamePerson,
  IncomingCallCFSFormValues,
} from '@/models';
import { useToastStore } from '@/store';
import { openNewWindow } from '@/utils';

import { IncomingCallDefaultValues } from '../data';
import { AssociatedAddressForm } from './AssociatedAddressForm';
import { AssociatedReporterForm } from './AssociatedReporterForm';
import { DispositionForm, DispositionFormValues } from './DispositionForm';
import { NotesForm } from './Notes';
import { RecentCFSList } from './RecentCFSList';

interface IncomingCallCFSFormProps {
  phoneNumber: string;
  masterNames: IMasterNamePerson[];
  onClose: () => void;
  incomingCallId: string;
}

const IncomingCallCFSForm = ({
  phoneNumber,
  masterNames,
  incomingCallId,
  onClose,
  setIsDirty,
}: IncomingCallCFSFormProps & WithUnsavedChangesWarningProps) => {
  const { updateToast } = useToastStore();
  const methods = useForm<IncomingCallCFSFormValues>({
    defaultValues: IncomingCallDefaultValues,
    mode: 'all',
  });
  const { handleSubmit, reset } = methods;
  const addresses = masterNames
    .filter(({ address }) => !!address)
    .map(({ address }) => address) as IAddress[];

  useEffect(() => {
    reset({
      reporter: {
        callerPhoneNumber: phoneNumber,
      },
    });
  }, [phoneNumber]);

  useEffect(() => {
    setIsDirty(true);
    methods.setValue('disposition', DispositionFormValues.NEW_CFS.toString());
  }, []);

  const selectedDisposition = methods.watch('disposition');

  const onSubmit = async (values: IncomingCallCFSFormValues) => {
    const { address, primaryReporter, reporter, disposition, notes } = values;

    if (
      disposition === DispositionFormValues.WRONG_NUMBER ||
      disposition === DispositionFormValues.PHONY_CALL ||
      disposition === DispositionFormValues.SHIFT_CHANGE ||
      disposition === DispositionFormValues.TEST ||
      disposition === DispositionFormValues.OTHER ||
      disposition === DispositionFormValues.CREATED_IN_ERROR
    ) {
      try {
        await IncomingCallsApi.endCall(incomingCallId, {
          disposition,
          notes: notes ?? '',
        });

        onClose();
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
      }
    } else {
      try {
        const response = await CfsApi.createFromIncomingCall({
          address: address?._id,
          businessName: address?.businessName ?? undefined,
          primaryReporter: primaryReporter?._id,
          disposition,
          incomingCallNotes: notes,
          incomingCallId,
          reporter,
        });
        const cfsId = response.data._id;

        await openNewWindow(`/cfs/${cfsId}/?checkAddress=${true}`, cfsId);

        onClose();
      } catch (err: any) {
        updateToast({ open: true, message: err.message });
      }
    }
  };

  return (
    <FormProvider {...methods}>
      <FormRoot onSubmit={handleSubmit(onSubmit)} sx={{ padding: '1px' }}>
        <Grid container spacing={2.5}>
          <Grid item sm={12} xl={6}>
            <DispositionForm />
            <NotesForm />
            <AssociatedAddressForm addresses={addresses} />
            <AssociatedReporterForm masterNames={masterNames} />
          </Grid>
          <Grid item sm={12} xl={6}>
            <FormCard
              key="Recent CFSs and address"
              title="Recent CFSs and address"
              sx={{ py: 3 }}
            >
              <RecentCFSList
                incomingCallId={incomingCallId}
                onClose={onClose}
                getValues={methods.getValues}
              />
            </FormCard>
          </Grid>
        </Grid>
        <Stack className="btn-wrapper">
          <Button
            variant="contained"
            color="error"
            type="submit"
            disabled={
              selectedDisposition === DispositionFormValues.NEW_CFS ||
              selectedDisposition === DispositionFormValues.EXISTING_CFS
            }
          >
            Save and Close
          </Button>
          {/* <Button variant="contained" color="inherit" onClick={onClose}>
            Cancel
          </Button> */}
          <Button
            variant="contained"
            color="error"
            type="submit"
            disabled={
              selectedDisposition === DispositionFormValues.WRONG_NUMBER ||
              selectedDisposition === DispositionFormValues.PHONY_CALL ||
              selectedDisposition === DispositionFormValues.SHIFT_CHANGE ||
              selectedDisposition === DispositionFormValues.TEST ||
              selectedDisposition === DispositionFormValues.OTHER ||
              selectedDisposition === DispositionFormValues.CREATED_IN_ERROR
            }
          >
            Create CFS
          </Button>
        </Stack>
      </FormRoot>
    </FormProvider>
  );
};

export default withUnsavedChangesWarning(IncomingCallCFSForm);
